import { CustomField, Generic, ShortlistStatus } from '../dictionary-service';
import { SimpleProfile } from '../profile-service';
import { User } from '../recruiter-service';

export enum StatusCode {
  CUSTOM = 'custom',
  LONG_LIST = 'long_list',
  APPLIES = 'applies',
  INTERVIEW_PLANNED = 'interview_planned',
  SENT_TO_REVIEW = 'sent_to_review',
  NOT_ACCEPTED = 'not_accepted',
  AI = 'ai',
  HIRED = 'hired',
  CHAT_BOT_PASSED = 'chat_bot_passed',
  RESERVE = 'reserve',
}

export type Status = Omit<ShortlistStatus.Item, 'type' | 'shortlistCount'>;

export interface Base {
  readonly id: number;
  readonly status: Status;
  readonly vacancy: Generic.Item;
}

export interface Short extends Omit<Base, 'status'> {
  readonly recruiter: string;
  readonly status: string;
  readonly updatedAt: string;
}

export interface Item extends Base {
  readonly author: User.Base;
  readonly comment: string | null;
  readonly contacts: string | null;
  readonly commission: number | null;
  readonly createdAt: string;
  readonly cvSentDate: string | null;
  readonly customFields: CustomField.Item[];
  readonly hiredAt: string | null;
  readonly interviewAt: string | null;
  readonly interviewDate: string | null;
  readonly profileId: number;
  readonly rate: number | null;
  readonly rejectedDate: string | null;
  readonly rejectReason: Generic.Item | null;
  readonly salary: number | null;
  readonly source: Generic.Item;
  readonly statusChangedAt: string | null;
  readonly statusChangedAuthor: User.Base | null;
  readonly updatedAt: string | null;
  readonly updatedAuthor: User.Base | null;
  readonly vacancy: Generic.Item;
}

// Internal model, not exists on service side
export interface Application extends Item {
  readonly portalId: number;
  readonly profile: SimpleProfile;
  readonly vacancy: Generic.Item & { assignedId: number };
}

export interface ApplyPayload {
  readonly authorId: number;
  readonly comment: string;
  readonly contacts?: string;
  readonly portalId: number;
  readonly profileId: number;
  readonly projectId: number;
  readonly salary: number;
  readonly sourceId: number;
  /** Optional target status id; when omitted, the default Apply status is used. */
  readonly statusId?: number;
}

export interface CreatePayload {
  readonly comment: string | null;
  readonly commission: number | null;
  readonly contacts: string | null;
  readonly customFields: CustomField.Item[];
  readonly profileId: number;
  readonly rate: number | null;
  readonly salary: number | null;
  readonly sendUpdateToTelegram: boolean;
  readonly sourceCode: string;
  readonly statusId: number;
  readonly vacancyId: number;
}

export interface UpdatePayload extends Omit<CreatePayload, 'sourceCode'> {
  readonly authorId: number;
  readonly cvSentDate: string | null;
  readonly hiredAt: string | null;
  readonly id: number;
  readonly interviewAt: string | null;
  readonly rejectedDate: string | null;
  readonly rejectReasonId: number | null;
  readonly sourceId: number;
}

export interface PatchPayload {
  readonly id: number;
  readonly customFields: CustomField.Item[];
  readonly statusId: number;
}

export interface MassCreatePayload {
  readonly profilesIds: number[];
  readonly statusId: number;
  readonly sourceId: number;
  readonly vacancyId: number;
}

export interface MultipleUpdatePayload {
  readonly ids: number[];
  readonly statusId: number;
  readonly vacancyId: number;
  readonly cvSentDate?: string | null;
  readonly hiredAt?: string | null;
  readonly interviewAt?: string | null;
  readonly rejectedDate?: string | null;
  readonly rejectReasonId?: number | null;
}

export interface MassActionPayload {
  readonly ids: number[];
  readonly sourceVacancyId: number;
  readonly targetVacancyId: number;
  readonly action: 'copy' | 'move';
}
